/**
 * Service d'intégration Daily.co pour les visioconférences AG
 *
 * Ce service gère la création, mise à jour et suppression des rooms Daily.co
 * liées aux assemblées générales.
 */

import { addHours, subMinutes } from 'date-fns'
import { supabase } from '@/lib/supabase'

// ===== CONFIGURATION =====

export const DAILY_CONFIG = {
  baseUrl: 'https://api.daily.co/v1',
  domain: 'kopro', // kopro.daily.co

  // Paramètres par défaut des rooms AG
  defaultRoomConfig: {
    privacy: 'private' as const,
    enable_recording: 'none' as const,
    enable_knocking: true,
    enable_screenshare: true,
    enable_chat: true,
    start_video_off: false,
    start_audio_off: true, // Mute par défaut pour éviter le bruit
  },

  // Limites de sécurité
  limits: {
    accessBeforeMinutes: 30, // Ouverture 30min avant
    expiresAfterHours: 6, // Fermeture 6h après
    maxExtensionHours: 4, // Extension max +4h
    absoluteMaxHours: 10, // Limite absolue 10h
  },
}

// ===== TYPES =====

export interface DailyRoomResponse {
  id: string
  name: string
  url: string
  api_created: boolean
  privacy: string
  config: {
    nbf?: number
    exp?: number
    max_participants?: number
    enable_knocking?: boolean
    enable_screenshare?: boolean
    enable_chat?: boolean
    start_video_off?: boolean
    start_audio_off?: boolean
    enable_recording?: string
  }
  created_at: string
}

export interface CreateVideoRoomParams {
  meetingId: string
  meetingDate: string // Format YYYY-MM-DD
  startTime: string // Format HH:MM ou HH:MM:SS
  meetingType: string // ordinary, extraordinary, mixed
  agencySlug: string
  residenceKey: string
  expectedParticipants: number
  apiKey: string
}

export interface UpdateVideoRoomParams {
  roomName: string
  newMeetingDate: Date
  apiKey: string
}

export interface VideoRoom {
  id: string
  meeting_id: string
  daily_room_name: string
  daily_room_url: string
  access_start_at: string
  expires_at: string
  max_participants: number
  status: 'pending' | 'active' | 'ended' | 'expired' | 'cancelled'
  started_at?: string | null
  ended_at?: string | null
  participant_count?: number | null
  duration_minutes?: number | null
  created_at: string
  updated_at: string
}

export interface DailyMeetingToken {
  token: string
}

export interface CreateMeetingTokenParams {
  roomName: string
  userName: string
  isOwner: boolean
  expiresAt: Date
  apiKey: string
}

// ===== HELPER FUNCTIONS =====

/**
 * Génère le nom de la room Daily.co
 * Format: [AGENCY-SLUG]-[RESIDENCE-KEY]-[TYPE-AG]-[DATE-AG]
 * Ex: cabinet-mh-MH001-ordinaire-20251215
 */
function generateRoomName(
  agencySlug: string,
  residenceKey: string,
  meetingType: string,
  meetingDate: string
): string {
  // Nettoyer et formater les composants
  const slug = agencySlug
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .substring(0, 15)
  const resKey = residenceKey
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .substring(0, 10)
  const type = meetingType === 'ordinary' ? 'ord' : meetingType === 'extraordinary' ? 'ext' : 'mix'
  // Extraire uniquement la partie date (YYYY-MM-DD) et retirer les tirets
  const dateOnly = meetingDate.substring(0, 10).replace(/-/g, '')

  return `${slug}-${resKey}-${type}-${dateOnly}`
}

/**
 * Calcule les timestamps d'accès pour la room
 */
function calculateAccessWindow(meetingDate: Date): {
  nbf: number // Unix timestamp (seconds)
  exp: number // Unix timestamp (seconds)
  accessStartAt: Date
  expiresAt: Date
} {
  const accessStartAt = subMinutes(meetingDate, DAILY_CONFIG.limits.accessBeforeMinutes)
  const expiresAt = addHours(meetingDate, DAILY_CONFIG.limits.expiresAfterHours)

  return {
    nbf: Math.floor(accessStartAt.getTime() / 1000),
    exp: Math.floor(expiresAt.getTime() / 1000),
    accessStartAt,
    expiresAt,
  }
}

// ===== DAILY.CO API FUNCTIONS =====

/**
 * Crée une room chez Daily.co
 */
async function createDailyRoom(
  roomName: string,
  nbf: number,
  exp: number,
  maxParticipants: number,
  apiKey: string
): Promise<DailyRoomResponse> {
  const response = await fetch(`${DAILY_CONFIG.baseUrl}/rooms`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: roomName,
      privacy: 'private',
      properties: {
        nbf,
        exp,
        max_participants: maxParticipants,
        enable_knocking: true,
        enable_screenshare: true,
        enable_chat: true,
        start_video_off: false,
        start_audio_off: true,
      },
    }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Daily.co API error: ${response.status} - ${errorText}`)
  }

  return response.json()
}

/**
 * Met à jour une room chez Daily.co
 */
async function updateDailyRoom(
  roomName: string,
  nbf: number,
  exp: number,
  apiKey: string
): Promise<DailyRoomResponse> {
  const response = await fetch(`${DAILY_CONFIG.baseUrl}/rooms/${roomName}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      properties: {
        nbf,
        exp,
      },
    }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Daily.co API error: ${response.status} - ${errorText}`)
  }

  return response.json()
}

/**
 * Supprime une room chez Daily.co
 */
async function deleteDailyRoom(roomName: string, apiKey: string): Promise<void> {
  console.log('🗑️ [Daily.co] Suppression de la room:', roomName)
  console.log('🔗 [Daily.co] URL:', `${DAILY_CONFIG.baseUrl}/rooms/${roomName}`)

  const response = await fetch(`${DAILY_CONFIG.baseUrl}/rooms/${roomName}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${apiKey}`,
    },
  })

  console.log('📡 [Daily.co] Response status:', response.status)

  // 404 = room déjà supprimée, on ignore
  if (!response.ok && response.status !== 404) {
    const errorText = await response.text()
    console.error('❌ [Daily.co] Erreur:', errorText)
    throw new Error(`Daily.co API error: ${response.status} - ${errorText}`)
  }

  if (response.status === 404) {
    console.log('ℹ️ [Daily.co] Room non trouvée (404) - probablement déjà supprimée')
  } else if (response.ok) {
    console.log('✅ [Daily.co] Room supprimée avec succès')
  }
}

// ===== MAIN SERVICE FUNCTIONS =====

/**
 * Crée une room visio pour une AG
 *
 * Cette fonction :
 * 1. Crée la room chez Daily.co
 * 2. Enregistre les infos dans video_rooms
 * 3. Met à jour meetings.video_room_url
 */
export async function createVideoRoom(params: CreateVideoRoomParams): Promise<VideoRoom> {
  const { meetingId, meetingDate, startTime, meetingType, agencySlug, residenceKey, expectedParticipants, apiKey } = params

  // Générer le nom de la room
  const roomName = generateRoomName(agencySlug, residenceKey, meetingType, meetingDate)

  // Combiner date et heure pour obtenir le datetime complet du meeting
  const timeFormatted = startTime.length > 5 ? startTime.substring(0, 5) : startTime

  // Debug: afficher les valeurs
  console.log('📅 meetingDate:', meetingDate, 'startTime:', startTime, 'timeFormatted:', timeFormatted)

  // Construire la date - extraire la partie date si c'est un datetime ISO
  let dateStr: string = meetingDate
  if (meetingDate.includes('T')) {
    // Si c'est déjà un datetime ISO, extraire juste la date
    const parts = meetingDate.split('T')
    dateStr = parts[0] || meetingDate
  }

  const meetingDateTime = new Date(`${dateStr}T${timeFormatted}:00`)
  console.log('📅 meetingDateTime:', meetingDateTime, 'isValid:', !isNaN(meetingDateTime.getTime()))

  // Calculer la fenêtre d'accès
  const { nbf, exp, accessStartAt, expiresAt } = calculateAccessWindow(meetingDateTime)

  // Calculer le max de participants (copropriétaires + 20%)
  const maxParticipants = Math.ceil(expectedParticipants * 1.2)

  // 1. Créer la room chez Daily.co
  const dailyRoom = await createDailyRoom(roomName, nbf, exp, maxParticipants, apiKey)

  // 2. Enregistrer en base
  // Note: Cast temporaire - les types Supabase seront mis à jour après exécution de la migration
  const { data: videoRoom, error } = await (supabase as any)
    .from('video_rooms')
    .insert({
      meeting_id: meetingId,
      daily_room_name: dailyRoom.name,
      daily_room_url: dailyRoom.url,
      access_start_at: accessStartAt.toISOString(),
      expires_at: expiresAt.toISOString(),
      max_participants: maxParticipants,
      status: 'pending',
    })
    .select()
    .single()

  if (error) {
    // Si l'insertion échoue, supprimer la room Daily.co
    await deleteDailyRoom(dailyRoom.name, apiKey).catch(console.error)
    throw new Error(`Erreur base de données: ${error.message}`)
  }

  // 3. Mettre à jour le meeting avec l'URL
  // Note: Cast temporaire - les types Supabase seront mis à jour après exécution de la migration
  const { error: updateError } = await (supabase as any)
    .from('meetings')
    .update({ video_room_url: dailyRoom.url })
    .eq('id', meetingId)

  if (updateError) {
    console.error('Erreur mise à jour meeting.video_room_url:', updateError)
  }

  return videoRoom as VideoRoom
}

/**
 * Met à jour une room visio (en cas de report d'AG)
 */
export async function updateVideoRoom(params: UpdateVideoRoomParams): Promise<void> {
  const { roomName, newMeetingDate, apiKey } = params

  // Calculer les nouveaux timestamps
  const { nbf, exp, accessStartAt, expiresAt } = calculateAccessWindow(newMeetingDate)

  // 1. Mettre à jour chez Daily.co
  await updateDailyRoom(roomName, nbf, exp, apiKey)

  // 2. Mettre à jour en base
  // Note: Cast temporaire - les types Supabase seront mis à jour après exécution de la migration
  const { error } = await (supabase as any)
    .from('video_rooms')
    .update({
      access_start_at: accessStartAt.toISOString(),
      expires_at: expiresAt.toISOString(),
    })
    .eq('daily_room_name', roomName)

  if (error) {
    throw new Error(`Erreur base de données: ${error.message}`)
  }
}

/**
 * Supprime une room visio (en cas de désactivation ou annulation d'AG)
 *
 * Cette fonction supprime complètement:
 * 1. La room chez Daily.co
 * 2. L'enregistrement video_rooms en base
 * 3. L'URL video_room_url sur le meeting
 */
export async function deleteVideoRoom(
  meetingId: string,
  roomName: string,
  apiKey: string
): Promise<void> {
  // 1. Supprimer chez Daily.co
  await deleteDailyRoom(roomName, apiKey)

  // 2. Supprimer l'enregistrement en base (pas juste update du status)
  const { error } = await (supabase as any)
    .from('video_rooms')
    .delete()
    .eq('meeting_id', meetingId)

  if (error) {
    throw new Error(`Erreur base de données: ${error.message}`)
  }

  // 3. Effacer l'URL du meeting
  await (supabase as any).from('meetings').update({ video_room_url: null }).eq('id', meetingId)
}

/**
 * Récupère la room visio d'une AG
 */
export async function getVideoRoomByMeetingId(meetingId: string): Promise<VideoRoom | null> {
  // Note: Cast temporaire - les types Supabase seront mis à jour après exécution de la migration
  const { data, error } = await (supabase as any)
    .from('video_rooms')
    .select('*')
    .eq('meeting_id', meetingId)
    .maybeSingle() // Utiliser maybeSingle() au lieu de single() pour éviter l'erreur PGRST116

  if (error) {
    throw new Error(`Erreur base de données: ${error.message}`)
  }

  return data as VideoRoom | null
}

/**
 * Prolonge une room visio (ajoute du temps)
 */
export async function extendVideoRoom(
  roomName: string,
  extensionHours: number,
  apiKey: string
): Promise<void> {
  // Récupérer la room actuelle
  // Note: Cast temporaire - les types Supabase seront mis à jour après exécution de la migration
  const { data: currentRoom, error: fetchError } = await (supabase as any)
    .from('video_rooms')
    .select('expires_at')
    .eq('daily_room_name', roomName)
    .single()

  if (fetchError || !currentRoom) {
    throw new Error('Room non trouvée')
  }

  const currentExpiry = new Date(currentRoom.expires_at)
  const newExpiry = addHours(currentExpiry, extensionHours)

  // Vérifier la limite absolue (10h après la date de début)
  // Note: Cast temporaire - les types Supabase seront mis à jour après exécution de la migration
  const { data: roomData } = await (supabase as any)
    .from('video_rooms')
    .select('access_start_at')
    .eq('daily_room_name', roomName)
    .single()

  if (roomData) {
    const startTime = new Date(roomData.access_start_at)
    const absoluteMax = addHours(startTime, DAILY_CONFIG.limits.absoluteMaxHours)

    if (newExpiry > absoluteMax) {
      throw new Error(`Extension impossible: durée maximale de ${DAILY_CONFIG.limits.absoluteMaxHours}h atteinte`)
    }
  }

  // Calculer le nouveau timestamp exp
  const newExp = Math.floor(newExpiry.getTime() / 1000)

  // 1. Mettre à jour chez Daily.co
  const response = await fetch(`${DAILY_CONFIG.baseUrl}/rooms/${roomName}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      properties: {
        exp: newExp,
      },
    }),
  })

  if (!response.ok) {
    throw new Error('Erreur lors de la prolongation chez Daily.co')
  }

  // 2. Mettre à jour en base
  // Note: Cast temporaire - les types Supabase seront mis à jour après exécution de la migration
  const { error } = await (supabase as any)
    .from('video_rooms')
    .update({ expires_at: newExpiry.toISOString() })
    .eq('daily_room_name', roomName)

  if (error) {
    throw new Error(`Erreur base de données: ${error.message}`)
  }
}

/**
 * Marque une room comme terminée
 */
export async function endVideoRoom(meetingId: string): Promise<void> {
  // Note: Cast temporaire - les types Supabase seront mis à jour après exécution de la migration
  const { error } = await (supabase as any)
    .from('video_rooms')
    .update({
      status: 'ended',
      ended_at: new Date().toISOString(),
    })
    .eq('meeting_id', meetingId)

  if (error) {
    throw new Error(`Erreur base de données: ${error.message}`)
  }
}

/**
 * Génère un meeting token Daily.co pour rejoindre une room
 *
 * @param isOwner - true pour le gestionnaire/animateur (host), false pour les participants
 */
export async function createMeetingToken(params: CreateMeetingTokenParams): Promise<string> {
  const { roomName, userName, isOwner, expiresAt, apiKey } = params

  const response = await fetch(`${DAILY_CONFIG.baseUrl}/meeting-tokens`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      properties: {
        room_name: roomName,
        user_name: userName,
        is_owner: isOwner,
        exp: Math.floor(expiresAt.getTime() / 1000),
        enable_screenshare: true,
        start_video_off: false,
        start_audio_off: !isOwner, // Host démarre avec le micro, participants mutés
      },
    }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Daily.co API error: ${response.status} - ${errorText}`)
  }

  const data = await response.json()
  return data.token
}

/**
 * Génère l'URL complète pour rejoindre une room avec un token
 */
export function buildRoomUrlWithToken(roomUrl: string, token: string): string {
  return `${roomUrl}?t=${token}`
}
